import { Link, usePage } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import { BlogLayout } from './BlogLayout';
import PremiumContentModal from '@/components/PremiumContentModal';
import { BlogPostCard, type BlogPost } from '@/components/blog/BlogPostCard';

// Types
interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  visibility: string; // 'visible' or 'auth_only'
  status: string; // 'published', 'draft', etc.
  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  } | null;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  postCount: number;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
  postCount: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  nextPage: number | null;
  prevPage: number | null;
}

interface SEO {
  title: string;
  description: string;
  og_title: string;
  og_description: string;
  og_type: string;
  twitter_card: string;
}

interface PageProps extends Record<string, unknown> {
  publicPosts?: Post[];
  memberPosts?: Post[];
  featuredVisible?: Post | null;
  featuredAuthOnly?: Post | null;
  categories?: Category[];
  tags?: Tag[];
  pagination?: Pagination;
  memberPagination?: Pagination;
  seo?: SEO;
  auth?: {
    user?: {
      id: number;
      email: string;
      name: string;
      role: string;
    };
  };
}

// Pagination Component
function Pagination({ pagination }: { pagination: Pagination }) {
  const { currentPage, totalPages, prevPage, nextPage } = pagination;
  
  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center items-center space-x-2 mt-12">
      {prevPage ? (
        <Link
          href={`/blog${prevPage > 1 ? `/page/${prevPage}` : ''}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Previous
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Previous
        </span>
      )}
      
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <Link
            key={page}
            href={`/blog${page > 1 ? `/page/${page}` : ''}`}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              page === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-muted'
            }`}
          >
            {page}
          </Link>
        ))}
      </div>
      
      {nextPage ? (
        <Link
          href={`/blog/page/${nextPage}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Next
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Next
        </span>
      )}
    </div>
  );
}

// Category Filter Component
function CategoryFilter({ categories, activeCategory }: { categories: Category[]; activeCategory?: string }) {
  return (
    <div className="flex flex-wrap gap-2 mb-8">
      <Link
        href="/blog"
        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
          !activeCategory
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        All
      </Link>
      {categories.map((category) => (
        <Link
          key={category.id}
          href={`/blog/category/${category.slug}`}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            activeCategory === category.slug
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground hover:bg-muted/80'
          }`}
        >
          {category.name}
        </Link>
      ))}
    </div>
  );
}

// Main Index Page
export default function BlogIndex() {
  const props = usePage<PageProps>().props;
  const publicPosts = props.publicPosts || [];
  const memberPosts = props.memberPosts || [];
  const featuredVisible = props.featuredVisible;
  const featuredAuthOnly = props.featuredAuthOnly;
  const categories = props.categories || [];
  const pagination = props.pagination || { currentPage: 1, totalPages: 1, totalCount: 0, perPage: 12, nextPage: null, prevPage: null };
  const memberPagination = props.memberPagination || { currentPage: 1, totalPages: 1, totalCount: 0, perPage: 12, nextPage: null, prevPage: null };
  const [selectedAuthPost, setSelectedAuthPost] = useState<Post | null>(null);

  // Check for auth_required query param on mount (from redirect after trying to access auth_only post)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const authRequiredSlug = params.get('auth_required');
      const authRequiredTitle = params.get('title');
      
      if (authRequiredSlug && !props.auth?.user) {
        // Find the post in memberPosts or create a minimal post object
        const foundPost = memberPosts.find(p => p.slug === authRequiredSlug);
        if (foundPost) {
          setSelectedAuthPost(foundPost);
        } else if (authRequiredTitle) {
          // Create a minimal post object if not found in the list
          setSelectedAuthPost({
            id: 0,
            title: authRequiredTitle,
            slug: authRequiredSlug,
            summary: '',
            content: '',
            coverImageUrl: null,
            publishedAt: null,
            readingTimeMinutes: 5,
            visibility: 'auth_only',
            status: 'published',
            author: null,
            categories: [],
            tags: []
          });
        }
        
        // Clean up URL by removing the query params (optional - keeps URL clean)
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      }
    }
  }, [memberPosts, props.auth?.user]);

  // Handle click on auth-only post - show modal for non-logged-in users
  const handlePostClick = (post: Post, e: React.MouseEvent) => {
    if (post.visibility === 'auth_only' && !props.auth?.user) {
      e.preventDefault();
      setSelectedAuthPost(post);
    }
  };

  return (
    <BlogLayout>
      <div className="space-y-12">
        {/* Featured Posts - one visible, one auth_only - styled like Members Only section */}
        {(featuredVisible || featuredAuthOnly) && pagination.currentPage === 1 && (
          <section className="bg-muted/30 dark:bg-muted/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-foreground">Featured</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {featuredVisible && <BlogPostCard post={featuredVisible as BlogPost} onClick={(e) => handlePostClick(featuredVisible, e)} />}
              {featuredAuthOnly && <BlogPostCard post={featuredAuthOnly as BlogPost} onClick={(e) => handlePostClick(featuredAuthOnly, e)} />}
            </div>
          </section>
        )}

        {/* Category Filter */}
        <section>
          <CategoryFilter categories={categories} />
        </section>

        {/* Public Articles Section */}
        <section className="bg-muted/30 dark:bg-muted/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold text-foreground">Articles</h2>
            <span className="text-sm text-muted-foreground">({publicPosts.length} posts)</span>
          </div>
          
          {publicPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicPosts.map((post) => (
                <BlogPostCard key={post.id} post={post as BlogPost} onClick={(e) => handlePostClick(post, e)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <h3 className="text-lg font-medium text-foreground mb-2">No posts yet</h3>
              <p className="text-muted-foreground">Check back soon for new content!</p>
            </div>
          )}
        </section>

        {/* Members Only Section - always show if there are auth_only posts */}
        {memberPosts.length > 0 && (
          <section className="bg-muted/30 dark:bg-muted/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h2 className="text-2xl font-bold text-foreground">Members Only</h2>
              <span className="text-sm text-muted-foreground">({memberPosts.length} posts)</span>
            </div>
            {!props.auth?.user && (
              <p className="text-muted-foreground mb-4">Sign in to access these exclusive articles.</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {memberPosts.map((post) => (
                <BlogPostCard key={post.id} post={post as BlogPost} onClick={(e) => handlePostClick(post, e)} />
              ))}
            </div>
          </section>
        )}

        {/* Pagination */}
        <Pagination pagination={pagination} />
      </div>

      {/* Premium Content Modal for Auth-Only Posts */}
      <PremiumContentModal
        isOpen={!!selectedAuthPost}
        onClose={() => setSelectedAuthPost(null)}
        postTitle={selectedAuthPost?.title || ''}
        postSlug={selectedAuthPost?.slug}
      />
    </BlogLayout>
  );
}
